import * as React from "react";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { CustomerNameFuzzySearchClient } from "../../../../../../../Wrap/Api/Customer/CustomerNameFuzzySearch";
import { RoleType } from "../../../../../../../Wrap/Constants/Enums/RoleType";
import { FullClientDetailModel } from "../../../../../../../Wrap/Models/Models/Customer/FullClientDetailModel";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { SearchInput } from "../../../../../../Shared/SearchInput/SearchInput";
import { Table } from "../../../../../../Shared/Table/Table";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { FullClientDetailHelper } from "../../../Common/FullClientDetail/FullClientDetailHelper";
import { NewBusinessAction } from "../../Actions/NewBusinessAction";
import { ContactDetail } from "../../../NewBusinessWizard/Components/FullClientDetail/ContactDetail";
import { ClientDetail } from "../../../NewBusinessWizard/Components/FullClientDetail/ClientDetail";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { TaxDetail } from "../FullClientDetail/TaxDetail";
import { setValueByKey } from "../../../../../../Shared/Table/Utils/TableUtils";
import { ClientDetails } from "../../../../../../../Wrap/Models/Models/Processes/Quote/ClientDetails";
import { PhysicalAddressType } from "../../../../../../../Wrap/Constants/Enums/Customer/PhysicalAddressType";
import { CountryCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CountryCode";

interface IJointAccountHolderProps extends IProcessComponentProps {
    selectedClientInfo: FullClientDetailModel;
    onSelectClientInfoChange: (data: FullClientDetailModel) => void;
}

interface IJointAccountHolderState {
    selectedClientInfo: FullClientDetailModel;
    validationMssage: string[];
    hideResult: boolean;
}

export class JointAccountHolder extends ProcessComponent<IJointAccountHolderProps, IJointAccountHolderState> {
    constructor(props: IJointAccountHolderProps) {
        super(props);
        this.state = {
            selectedClientInfo: props.selectedClientInfo,
            validationMssage: [],
            hideResult: false,
        };
    }

    private initClient = (selectedClientInfo: FullClientDetailModel) => {
        let initArray = [];
        initArray = FullClientDetailHelper.initTaxModel(initArray, selectedClientInfo);
        initArray = FullClientDetailHelper.updatePostalAddress(initArray, selectedClientInfo);
        this.onFullClientChange(initArray);
    };

    componentDidUpdate(prevProps: IJointAccountHolderProps) {
        super.componentDidUpdate(prevProps);
        const newData = this.props.selectedClientInfo;
        const prevData = prevProps.selectedClientInfo;
        if (prevData == null && newData != null) {
            this.setState({ selectedClientInfo: newData }, () => this.initClient(newData));
        }
    }

    private onFullClientChange = (changes: { value: any; path: string }[]) => {
        const { selectedClientInfo } = this.state;
        let _model = { ...selectedClientInfo };
        changes.forEach((change) => {
            _model = setValueByKey(_model, change.path, change.value);
        });
        this.setState({ selectedClientInfo: _model });
        this.props.onSelectClientInfoChange(_model);
    };

    private transformInputSearch(data: ClientDetails[]) {
        const customerId = Number(getQueryParameters()["customerid"]);
        const filteredData = data != null ? data.filter((x) => x.ActorId != customerId) : null;

        if (filteredData) {
            const customeChildren = (
                <Table
                    data={filteredData}
                    col={[
                        {
                            title: "Client",
                            key: "Name",
                            render: (data, row, index) => {
                                return (
                                    <span
                                        style={{
                                            overflowWrap: "break-word",
                                            width: "120px",
                                            display: "inline-block",
                                            whiteSpace: "pre-wrap",
                                        }}
                                    >
                                        {row.FirstName + " " + row.LastName}
                                    </span>
                                );
                            },
                        },
                        {
                            title: "Date of birth",
                            key: "DateOfBirth",
                            render: (data, row, index) => {
                                return JsonDateParser.getFormatDateString(new Date(data), "dd MM yyyy", true);
                            },
                        },
                        {
                            title: "Email address",
                            key: "EmailAddress",
                        },
                        {
                            title: "",
                            key: "",
                            render: (data, row, index) => {
                                return (
                                    <button className="secondary-add-button" onClick={() => this.addClient(row)}>
                                        <span className="text">+ add client</span>
                                    </button>
                                );
                            },
                        },
                    ]}
                    showEmptyMsg={true}
                />
            );
            return this.state.hideResult ? null : customeChildren;
        }
    }

    private addClient(row: ClientDetails) {
        const SecondClientDetails = row;
        this.props.onChange({ SecondClientDetails: SecondClientDetails });
        this.setHideSearchCustomerResult();
        NewBusinessAction.accountHolderAction(SecondClientDetails?.ActorId, (result) => {
            this.setState({ selectedClientInfo: result }, () => this.initClient(result));
        });
    }

    isValid() {
        const flag = super.isValid();
        let { isValid, errorMsg } = FullClientDetailHelper.fullClientDetailValidation(this.state.selectedClientInfo);
        if (
            this.state.selectedClientInfo?.AddressDetails?.find(
                (ele) => ele.PhysicalAddressType == PhysicalAddressType.Residential
            )?.Address?.Country != CountryCode.GBR
        ) {
            errorMsg = errorMsg.concat([
                "You must hold a UK Residential address in order to create a new account or sub-account.",
            ]);
            isValid = false;
        }
        if (errorMsg.length > 0) {
            this.setState({ validationMssage: errorMsg });
        }
        return flag && isValid;
    }

    public Save = () => {
        if (!this.isValid()) {return;}
        const _info = FullClientDetailHelper.beforeSave(JSON.parse(JSON.stringify(this.state.selectedClientInfo)));
        NewBusinessAction.saveCCMDetail(this.state.selectedClientInfo?.ActorId, _info, () => {
            const _data = this.state.selectedClientInfo;
            _data.CustomerTaxResidencyDetails = _info.CustomerTaxResidencyDetails;
            this.setState({ selectedClientInfo: _data });
        });
    };

    private setHideSearchCustomerResult(hideResult = true) {
        this.setState({ hideResult });
    }

    public render(): JSX.Element {
        const { selectedClientInfo } = this.state;

        const client = new CustomerNameFuzzySearchClient();

        return (
            <>
                <h3>Joint account details</h3>
                <SearchInput
                    apiClient={(v) => {
                        return client.get(v, this.props.data.AdviserHierarchyId.EntityId, RoleType.JointAccountHolder);
                    }}
                    onInputFocus={() => this.setHideSearchCustomerResult(false)}
                    minLength={3}
                    customeChildren={(data) => this.transformInputSearch(data)}
                    cssWrapper="mb-35 control-size-500"
                />
                {selectedClientInfo && (
                    <>
                        <Accordion label={"Joint account holder"} cssWrapper="accordion-total-clients" isOpen={true}>
                            <ClientDetail
                                data={selectedClientInfo}
                                onChange={(changes) => {
                                    this.props.onChange(changes);
                                }}
                                onCCMChange={(changes) => {
                                    this.onFullClientChange(changes);
                                }}
                                onRef={(ref) => this.appendChild(ref)}
                                disableWhenFullClient={false}
                                existingClientInfo={selectedClientInfo}
                                index={1}
                            />
                            <ContactDetail
                                data={selectedClientInfo}
                                onChange={(changes) => {
                                    this.props.onChange(changes);
                                }}
                                onCCMChange={(changes) => {
                                    this.onFullClientChange(changes);
                                }}
                                onRef={(ref) => this.appendChild(ref)}
                                disableWhenFullClient={false}
                                existingClientInfo={selectedClientInfo}
                                onCorrespondenceAddressChange={() => {}}
                            />

                            <TaxDetail
                                data={selectedClientInfo}
                                onChange={(changes) => {
                                    this.props.onChange(changes);
                                }}
                                onCCMChange={(changes) => {
                                    this.onFullClientChange(changes);
                                }}
                                onRef={(ref) => this.appendChild(ref)}
                                disableWhenFullClient={false}
                                existingClientInfo={selectedClientInfo}
                            />
                            <button
                                className="circle-remove-button"
                                onClick={() => {
                                    this.props.onChange({ SecondClientDetails: null });
                                    this.setState({ selectedClientInfo: null });
                                }}
                            >
                                <span className="icon" aria-hidden="true"></span>
                                <span className="text">Remove this client</span>
                            </button>
                            {this.state.validationMssage &&
                                this.state.validationMssage.map((msg) => (
                                    <Alert alertType={AlertType.Error} message={msg}></Alert>
                                ))}
                        </Accordion>

                        <hr></hr>
                    </>
                )}
            </>
        );
    }
}
